import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPalette, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import {
  getTeacherPresentation,
  patchTeacherPresentation,
  type TeacherPresentationRecord,
  type TeacherPresentationSlide,
} from "../auth/api-client";
import { useAuth } from "../auth/AuthContext";
import { AutoResizeTextarea } from "../components/AutoResizeTextarea";
import { LessonIconPickerModal } from "./LessonIconPickerModal";
import { triggerPresentationPdfDownload } from "./triggerPresentationPdfDownload";
import { PresentationIcon } from "./PresentationIcon";

function newId(): string {
  return crypto.randomUUID();
}

export default function TeacherPresentationEditorPage() {
  const { courseId, presentationId } = useParams<{ courseId: string; presentationId: string }>();
  const { getBearerToken } = useAuth();
  const bearer = getBearerToken();
  const [presentation, setPresentation] = useState<TeacherPresentationRecord | null>(null);
  const [savedJson, setSavedJson] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [iconPickerOpen, setIconPickerOpen] = useState(false);

  const dirty = presentation !== null && savedJson !== null && JSON.stringify(presentation) !== savedJson;

  const load = useCallback(async () => {
    if (!bearer || !courseId || !presentationId) return;
    setLoading(true);
    setError(null);
    try {
      const { presentation: p } = await getTeacherPresentation(bearer, courseId, presentationId);
      setPresentation(p);
      setSavedJson(JSON.stringify(p));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load presentation");
    } finally {
      setLoading(false);
    }
  }, [bearer, courseId, presentationId]);

  useEffect(() => {
    void load();
  }, [load]);

  function updateSlide(index: number, patch: Partial<TeacherPresentationSlide>) {
    setPresentation((prev) => {
      if (!prev) return prev;
      const slides = [...prev.slides];
      slides[index] = { ...slides[index]!, ...patch };
      return { ...prev, slides };
    });
  }

  function addSlide() {
    setPresentation((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        slides: [...prev.slides, { id: newId(), title: "New slide", body: [] }],
      };
    });
  }

  function removeSlide(index: number) {
    setPresentation((prev) => {
      if (!prev || prev.slides.length <= 1) return prev;
      return { ...prev, slides: prev.slides.filter((_, i) => i !== index) };
    });
  }

  async function handleSave() {
    if (!bearer || !courseId || !presentationId || !presentation) return;
    setSaving(true);
    setError(null);
    try {
      const { presentation: saved } = await patchTeacherPresentation(bearer, courseId, presentationId, {
        title: presentation.title,
        description: presentation.description,
        slides: presentation.slides,
        iconName: presentation.iconName,
        iconBgColor: presentation.iconBgColor,
        iconFgColor: presentation.iconFgColor,
      });
      setPresentation(saved);
      setSavedJson(JSON.stringify(saved));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  if (!courseId || !presentationId) {
    return (
      <div className="teacher-quiz-editor teacher-pad">
        <p className="teacher-panel-muted">Missing course or presentation.</p>
      </div>
    );
  }
  if (loading) {
    return (
      <div className="teacher-quiz-editor teacher-pad">
        <p className="teacher-panel-muted">Loading presentation…</p>
      </div>
    );
  }
  if (!presentation) {
    return (
      <div className="teacher-quiz-editor teacher-pad">
        <div className="warn-banner" role="alert">
          {error ?? "Presentation not found"}
        </div>
      </div>
    );
  }

  const previewUrl = `/teacher/courses/${encodeURIComponent(courseId)}/presentations/${encodeURIComponent(presentationId)}/preview`;

  return (
    <div className="teacher-quiz-editor teacher-pad">
      <header className="teacher-quiz-editor-head">
        <button
          type="button"
          className="teacher-quiz-icon-btn"
          title="Change presentation icon"
          onClick={() => setIconPickerOpen(true)}
        >
          <PresentationIcon
            title={presentation.title}
            iconName={presentation.iconName}
            bgColor={presentation.iconBgColor}
            fgColor={presentation.iconFgColor}
          />
          <span className="teacher-quiz-icon-btn-badge" aria-hidden>
            <FontAwesomeIcon icon={faPalette} />
          </span>
        </button>
        <div className="teacher-quiz-editor-head-copy">
          <h1 className="teacher-panel-title">Edit presentation</h1>
          <p className="teacher-panel-lede">{presentation.slides.length} slides</p>
        </div>
      </header>

      {error ? (
        <div className="warn-banner" role="alert">
          {error}
        </div>
      ) : null}

      <div className="teacher-field">
        <label className="teacher-field-label" htmlFor="presentation-title">
          Title
        </label>
        <input
          id="presentation-title"
          className="teacher-input"
          value={presentation.title}
          onChange={(e) => setPresentation((p) => (p ? { ...p, title: e.target.value } : p))}
        />
      </div>
      <div className="teacher-field">
        <label className="teacher-field-label" htmlFor="presentation-description">
          Description
        </label>
        <AutoResizeTextarea
          id="presentation-description"
          className="teacher-input"
          value={presentation.description ?? ""}
          onChange={(e) =>
            setPresentation((p) => (p ? { ...p, description: e.target.value || null } : p))
          }
        />
      </div>

      <ol className="presentation-editor-slides">
        {presentation.slides.map((slide, index) => (
          <li key={slide.id} className="presentation-editor-slide">
            <div className="presentation-editor-slide-head">
              <span className="muted small">Slide {index + 1}</span>
              <button
                type="button"
                className="btn secondary tight"
                disabled={presentation.slides.length <= 1}
                onClick={() => removeSlide(index)}
                aria-label={`Delete slide ${index + 1}`}
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
            <input
              className="teacher-input"
              value={slide.title}
              onChange={(e) => updateSlide(index, { title: e.target.value })}
              placeholder="Slide title"
            />
            <AutoResizeTextarea
              className="teacher-input"
              value={slide.body.join("\n")}
              onChange={(e) =>
                updateSlide(index, {
                  body: e.target.value.split("\n").map((line) => line.trimEnd()),
                })
              }
              placeholder="Talking points, one paragraph per line"
            />
            <AutoResizeTextarea
              className="teacher-input"
              value={slide.code ?? ""}
              onChange={(e) => updateSlide(index, { code: e.target.value || undefined })}
              placeholder="Optional Python example"
            />
            <AutoResizeTextarea
              className="teacher-input"
              value={slide.notes ?? ""}
              onChange={(e) => updateSlide(index, { notes: e.target.value || undefined })}
              placeholder="Speaker notes (teachers only)"
            />
          </li>
        ))}
      </ol>

      <div className="teacher-quiz-editor-actions">
        <button type="button" className="btn secondary" onClick={addSlide}>
          <FontAwesomeIcon icon={faPlus} /> Add slide
        </button>
        <Link className="btn secondary" to={previewUrl}>
          Preview
        </Link>
        <Link className="btn secondary" to={`${previewUrl}?present=1`}>
          Present
        </Link>
        <button
          type="button"
          className="btn secondary"
          disabled={presentation.slides.length === 0}
          onClick={() =>
            triggerPresentationPdfDownload({ title: presentation.title, slides: presentation.slides })
          }
        >
          Download PDF
        </button>
        <Link className="btn secondary" to={`/teacher/courses/${encodeURIComponent(courseId)}/manage`}>
          Back to course
        </Link>
        <button type="button" className="btn primary" disabled={!dirty || saving} onClick={() => void handleSave()}>
          {saving ? "Saving…" : dirty ? "Save" : "Saved"}
        </button>
      </div>

      <LessonIconPickerModal
        open={iconPickerOpen}
        heading="Choose presentation icon"
        selectedIconName={presentation.iconName ?? undefined}
        bgColor={presentation.iconBgColor ?? "#1b4d2e"}
        fgColor={presentation.iconFgColor ?? "#fdf9f3"}
        lessonId={presentation.id}
        title={presentation.title}
        onClose={() => setIconPickerOpen(false)}
        onSelect={(iconName) => {
          setPresentation((prev) =>
            prev
              ? {
                  ...prev,
                  iconName: iconName ?? "book-open",
                  iconBgColor: prev.iconBgColor ?? "#1b4d2e",
                  iconFgColor: prev.iconFgColor ?? "#fdf9f3",
                }
              : prev
          );
          setIconPickerOpen(false);
        }}
      />
    </div>
  );
}
